import { useState } from 'react';

import {
  assetColumns,
  assetDefaultSort,
} from '@/sections/attacks/attackPaths/components/wizards/columns/assetColumns';
import { SelectionTable } from '@/sections/attacks/attackPaths/components/wizards/components/SelectionTable';
import { WizardSearchInput } from '@/sections/attacks/attackPaths/components/wizards/components/WizardSearchInput';
import type { SearchableListItem } from '@/sections/attacks/attackPaths/components/wizards/hooks/useSearchableList';
import { useSearchableList } from '@/sections/attacks/attackPaths/components/wizards/hooks/useSearchableList';

interface HuntScopeStepProps {
  /** Selected asset keys, e.g. `#asset#example.com`. Empty ⇒ all active targets. */
  value: string[];
  onChange: (keys: string[]) => void;
}

/**
 * Scope step for the hunt launcher: a searchable, multi-select asset table that
 * lets the user restrict the hunt to specific assets. Reuses the same
 * search-input + selection-table stack as the scan-profile asset assignment
 * modal so the two flows feel identical.
 *
 * Empty selection ⇒ the hunt is scoped to all active targets in Guard — this is
 * the default, so leaving the table untouched keeps the prior behaviour.
 */
export function HuntScopeStep({ value, onChange }: HuntScopeStepProps) {
  const [searchTerm, setSearchTerm] = useState('');

  // value is the source of truth for selection; derive the Set the table needs.
  const selectedKeys = new Set(value);

  // Active-only scope fence: only active assets/seeds are selectable into a
  // hunt (which steers an autonomous offensive agent). `STARTS WITH 'A'` covers
  // the whole active family (A/AL/AP/AH). Class filter restricts to network,
  // DNS, CIDR, and web-application assets — excludes AD, cloud, and repos.
  const { items, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useSearchableList({
      type: 'asset',
      searchTerm,
      pageSize: 200,
      assetStatuses: ['A'],
      assetClasses: ['domain', 'tld', 'ipv4', 'ipv6', 'cidr', 'webapplication'],
    });

  const handleToggle = (item: SearchableListItem) => {
    onChange(
      value.includes(item.key)
        ? value.filter(k => k !== item.key)
        : [...value, item.key]
    );
  };

  const handleSelectAllVisible = () => {
    const next = new Set(value);
    for (const item of items) next.add(item.key);
    onChange([...next]);
  };

  const handleClearAll = () => onChange([]);

  return (
    <div className="flex flex-col gap-4">
      <WizardSearchInput
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="Search assets..."
        ariaLabel="Search assets to scope the hunt"
        autoFocus
      />

      <SelectionTable
        mode="multi"
        data={items}
        columns={assetColumns}
        selectedKeys={selectedKeys}
        onToggle={handleToggle}
        onSelectAllVisible={handleSelectAllVisible}
        onClearAll={handleClearAll}
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onLoadMore={fetchNextPage}
        defaultSort={assetDefaultSort}
        maxRows={7}
      />
    </div>
  );
}
